import { DocFolderKind, DocQueryBody, DocSort, DocTagFilter, DocTagOp, DocView } from './documents.models';

/** State of the Documents window, kept in the URL query string of the form (bookmark / share) */
export interface DocState {
  folder: DocFolderKind;
  /** virtual folders: a document type or a table (and a record of it) */
  type: string | null;
  tableId: number | null;
  recordId: number | null;
  q: string;
  nameOnly: boolean;
  ext: string | null;
  from: string | null;
  to: string | null;
  /** tag conditions (tag names, no prefix): all must match, or any of them (tagAny) */
  tags: DocTagFilter[];
  tagAny: boolean;
  /** technical filters: size in MB, text extraction status, more than one version, last change days */
  sizeMin: number | null;
  sizeMax: number | null;
  ocr: string | null;
  versioned: boolean;
  updatedFrom: string | null;
  updatedTo: string | null;
  sort: DocSort | null;
  asc: boolean | null;
  view: DocView;
  page: number;
  pageSize: number;
  /** document shown in the detail panel */
  doc: string | null;
}

export const PAGE_SIZES = [25, 50, 100, 200];
export const FOLDERS: DocFolderKind[] = ['all', 'mine', 'recent', 'favourites', 'trash'];
export const SORTS: DocSort[] = ['date', 'updated', 'name', 'size', 'type'];
/** extensions offered in the filter (any other can come from the URL) */
export const COMMON_EXTENSIONS = ['pdf', 'docx', 'xlsx', 'csv', 'txt', 'xml', 'png', 'jpg', 'zip'];

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const EXT = /^[A-Za-z0-9]{1,10}$/;
const TAG_KEY = /^META\|[A-Z0-9_]{1,60}\$?$/;
/** a tag name as typed (no prefix): letters, digits, underscore, spaces; "$" at the end for a record link value */
const TAG_NAME = /^[A-Za-z0-9_ ]{1,60}\$?$/;
export const OCR_STATUSES = ['DONE', 'PENDING', 'EMPTY', 'FAILED', 'SKIPPED', 'UNKNOWN'];

/** "my tag" -> "MY_TAG" (the name the server stores, without its META| prefix) */
export function tagName(name: string): string {
  return name.trim().replace(/^META\|/i, '').toUpperCase().replace(/\s+/g, '_');
}

function parseMb(value: string | null): number | null {
  if (!value || !/^\d{1,7}(\.\d{1,3})?$/.test(value)) return null;
  const n = parseFloat(value);
  return n > 0 ? n : null;
}

export function defaultState(): DocState {
  return {
    folder: 'all',
    type: null,
    tableId: null,
    recordId: null,
    q: '',
    nameOnly: false,
    ext: null,
    from: null,
    to: null,
    tags: [],
    tagAny: false,
    sizeMin: null,
    sizeMax: null,
    ocr: null,
    versioned: false,
    updatedFrom: null,
    updatedTo: null,
    sort: null,
    asc: null,
    view: 'list',
    page: 0,
    pageSize: 50,
    doc: null,
  };
}

function posInt(value: string | null): number | null {
  if (!value || !/^\d{1,9}$/.test(value)) return null;
  const n = parseInt(value, 10);
  return n > 0 ? n : null;
}

/** State from the URL; invalid values are dropped */
export function fromQueryParams(params: URLSearchParams): DocState {
  const s = defaultState();
  const folder = params.get('f') as DocFolderKind;
  if (FOLDERS.includes(folder)) s.folder = folder;
  const type = params.get('type');
  if (type && /^[A-Za-z0-9_\-]{1,60}$/.test(type)) s.type = type;
  s.tableId = posInt(params.get('table'));
  s.recordId = s.tableId ? posInt(params.get('record')) : null;
  s.q = (params.get('q') ?? '').slice(0, 200);
  s.nameOnly = params.get('name') === '1';
  const ext = params.get('ext');
  if (ext && EXT.test(ext)) s.ext = ext.toLowerCase();
  const from = params.get('from');
  if (from && DAY.test(from)) s.from = from;
  const to = params.get('to');
  if (to && DAY.test(to)) s.to = to;
  // t=KEY~op~value (key without META|), several; tagk / tagv: older links (one "contains")
  for (const t of params.getAll('t').slice(0, 10)) {
    const cond = parseTag(t);
    if (cond) s.tags.push(cond);
  }
  const tagKey = params.get('tagk');
  const tagValue = (params.get('tagv') ?? '').slice(0, 200);
  if (tagKey && TAG_KEY.test(tagKey) && tagValue) s.tags.push({ key: tagKey.slice(5), op: 'contains', value: tagValue });
  s.tagAny = params.get('tm') === 'any';
  s.sizeMin = parseMb(params.get('smin'));
  s.sizeMax = parseMb(params.get('smax'));
  const ocr = params.get('ocr');
  if (ocr && OCR_STATUSES.includes(ocr)) s.ocr = ocr;
  s.versioned = params.get('ver') === '1';
  const uFrom = params.get('ufrom');
  if (uFrom && DAY.test(uFrom)) s.updatedFrom = uFrom;
  const uTo = params.get('uto');
  if (uTo && DAY.test(uTo)) s.updatedTo = uTo;
  const sort = params.get('sort') as DocSort;
  if (SORTS.includes(sort) || sort === 'relevance') s.sort = sort;
  if (params.get('asc') === '1') s.asc = true;
  else if (params.get('asc') === '0') s.asc = false;
  if (params.get('view') === 'tiles') s.view = 'tiles';
  const size = posInt(params.get('size'));
  if (size && PAGE_SIZES.includes(size)) s.pageSize = size;
  s.page = Math.min(posInt(params.get('page')) ?? 0, 10000);
  const doc = params.get('doc');
  if (doc && doc.length <= 300) s.doc = doc;
  return s;
}

/** URL query string of a state (defaults left out) */
export function toQueryParams(s: DocState): URLSearchParams {
  const p = new URLSearchParams();
  if (s.folder !== 'all') p.set('f', s.folder);
  if (s.type) p.set('type', s.type);
  if (s.tableId) p.set('table', String(s.tableId));
  if (s.tableId && s.recordId) p.set('record', String(s.recordId));
  if (s.q) p.set('q', s.q);
  if (s.nameOnly) p.set('name', '1');
  if (s.ext) p.set('ext', s.ext);
  if (s.from) p.set('from', s.from);
  if (s.to) p.set('to', s.to);
  for (const t of s.tags) {
    if (isComplete(t)) p.append('t', `${tagName(t.key)}~${t.op}~${t.value}`);
  }
  if (s.tagAny) p.set('tm', 'any');
  if (s.sizeMin) p.set('smin', String(s.sizeMin));
  if (s.sizeMax) p.set('smax', String(s.sizeMax));
  if (s.ocr) p.set('ocr', s.ocr);
  if (s.versioned) p.set('ver', '1');
  if (s.updatedFrom) p.set('ufrom', s.updatedFrom);
  if (s.updatedTo) p.set('uto', s.updatedTo);
  if (s.sort) p.set('sort', s.sort);
  if (s.asc !== null) p.set('asc', s.asc ? '1' : '0');
  if (s.view !== 'list') p.set('view', s.view);
  if (s.pageSize !== 50) p.set('size', String(s.pageSize));
  if (s.page > 0) p.set('page', String(s.page));
  if (s.doc) p.set('doc', s.doc);
  return p;
}

export function toQueryBody(s: DocState): DocQueryBody {
  return {
    folder: s.folder,
    type: s.type,
    tableId: s.tableId,
    recordId: s.tableId ? s.recordId : null,
    q: s.q.trim() || null,
    nameOnly: s.nameOnly,
    ext: s.ext,
    from: s.from,
    to: s.to,
    tagKey: null,
    tagValue: null,
    tags: s.tags.filter(isComplete).map((t) => ({ ...t, key: tagName(t.key), value: t.value.trim() })),
    tagMatch: s.tagAny && s.tags.filter(isComplete).length > 1 ? 'any' : null,
    sizeMin: s.sizeMin ? Math.round(s.sizeMin * 1024 * 1024) : null,
    sizeMax: s.sizeMax ? Math.round(s.sizeMax * 1024 * 1024) : null,
    ocrStatus: s.ocr,
    versioned: s.versioned || null,
    updatedFrom: s.updatedFrom,
    updatedTo: s.updatedTo,
    sort: s.sort,
    asc: s.asc,
    offset: s.page * s.pageSize,
    limit: s.pageSize,
  };
}

/** Filters shown as removable chips */
/** tag conditions: 'tag:<index>' */
export type DocFilterKey = 'type' | 'table' | 'record' | 'q' | 'ext' | 'period' | 'size' | 'ocr' | 'versioned' | 'updated' | `tag:${number}`;

export const TAG_OPS: DocTagOp[] = ['contains', 'equals', 'set', 'unset'];

/** a condition that can be sent: a valid key, and a value unless the operator needs none */
export function isComplete(t: DocTagFilter): boolean {
  return TAG_NAME.test(t.key.trim()) && TAG_OPS.includes(t.op) && (t.op === 'set' || t.op === 'unset' || !!t.value.trim());
}

function parseTag(raw: string): DocTagFilter | null {
  const first = raw.indexOf('~');
  const second = first < 0 ? -1 : raw.indexOf('~', first + 1);
  if (second < 0) return null;
  const key = raw.slice(0, first);
  const op = raw.slice(first + 1, second) as DocTagOp;
  const value = raw.slice(second + 1, second + 201);
  const t = { key, op, value };
  return isComplete(t) ? t : null;
}

export function activeFilters(s: DocState): DocFilterKey[] {
  const out: DocFilterKey[] = [];
  if (s.type) out.push('type');
  if (s.tableId) out.push('table');
  if (s.tableId && s.recordId) out.push('record');
  if (s.q.trim()) out.push('q');
  if (s.ext) out.push('ext');
  if (s.from || s.to) out.push('period');
  if (s.sizeMin || s.sizeMax) out.push('size');
  if (s.ocr) out.push('ocr');
  if (s.versioned) out.push('versioned');
  if (s.updatedFrom || s.updatedTo) out.push('updated');
  s.tags.forEach((t, i) => {
    if (isComplete(t)) out.push(`tag:${i}`);
  });
  return out;
}

export function clearFilter(s: DocState, key: DocFilterKey): Partial<DocState> {
  switch (key) {
    case 'type':
      return { type: null };
    case 'table':
      return { tableId: null, recordId: null };
    case 'record':
      return { recordId: null };
    case 'q':
      return { q: '', nameOnly: false, sort: s.sort === 'relevance' ? null : s.sort };
    case 'ext':
      return { ext: null };
    case 'period':
      return { from: null, to: null };
    case 'size':
      return { sizeMin: null, sizeMax: null };
    case 'ocr':
      return { ocr: null };
    case 'versioned':
      return { versioned: false };
    case 'updated':
      return { updatedFrom: null, updatedTo: null };
    default:
      return { tags: s.tags.filter((_, i) => `tag:${i}` !== key) };
  }
}

/** "1.2 MB" (binary multiples, locale decimal separator) */
export function formatSize(bytes: number | null | undefined, locale?: string): string {
  if (bytes === null || bytes === undefined || bytes < 0) return '';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let u = 0;
  while (value >= 1024 && u < units.length - 1) {
    value /= 1024;
    u++;
  }
  const digits = u === 0 || value >= 100 ? 0 : 1;
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(value)} ${units[u]}`;
}

/** Date of a doc server ISO string (null when missing or invalid) */
export function parseDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/** Extension of a file name, lower case, or null */
export function extensionOf(name: string | null | undefined): string | null {
  if (!name) return null;
  const i = name.lastIndexOf('.');
  return i > 0 && i < name.length - 1 ? name.slice(i + 1).toLowerCase() : null;
}

/** Base name and extension (the extension is kept on rename) */
export function splitName(name: string): { base: string; ext: string } {
  const i = name.lastIndexOf('.');
  return i > 0 ? { base: name.slice(0, i), ext: name.slice(i) } : { base: name, ext: '' };
}
